import React, { useState, useEffect } from 'react';
import { adminApiService, MemeBlacklistEntry } from '../../services/adminApiService';

interface MemeBlacklistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MemeBlacklistModal: React.FC<MemeBlacklistModalProps> = ({ isOpen, onClose }) => {
  const [blacklist, setBlacklist] = useState<MemeBlacklistEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  
  // Form state
  const [baseSymbol, setBaseSymbol] = useState('');
  const [reason, setReason] = useState('');

  const isMobile = window.innerWidth <= 768;

  // Load blacklist when modal opens
  useEffect(() => {
    if (isOpen) {
      loadBlacklist();
    }
  }, [isOpen]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadBlacklist = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApiService.getMemeBlacklist();
      if (response.success) {
        setBlacklist(response.data || []);
      } else {
        setError(response.error || 'Failed to load blacklist');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load blacklist');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!baseSymbol || baseSymbol.trim().length < 2 || baseSymbol.trim().length > 10) {
      setError('Base symbol must be 2-10 characters');
      return;
    }

    // Check for duplicates
    const upperSymbol = baseSymbol.toUpperCase().trim();
    if (blacklist.some(entry => entry.baseSymbol.toUpperCase() === upperSymbol)) {
      setError(`"${upperSymbol}" is already in the blacklist`);
      return;
    }

    // Validate alphanumeric
    if (!/^[A-Z0-9]+$/i.test(upperSymbol)) {
      setError('Base symbol must contain only letters and numbers');
      return;
    }

    try {
      setAdding(true);
      setError(null);
      const response = await adminApiService.addMemeBlacklistEntry(upperSymbol, reason.trim() || undefined);
      if (response.success) {
        setSuccessMessage(`"${upperSymbol}" added to blacklist successfully`);
        setBaseSymbol('');
        setReason('');
        await loadBlacklist();
      } else {
        setError(response.error || 'Failed to add to blacklist');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add to blacklist');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (baseSymbolToRemove: string) => {
    if (!window.confirm(`Are you sure you want to remove "${baseSymbolToRemove}" from the blacklist?`)) {
      return;
    }

    try {
      setRemoving(baseSymbolToRemove);
      setError(null);
      const response = await adminApiService.removeMemeBlacklistEntry(baseSymbolToRemove);
      if (response.success) {
        setSuccessMessage(`"${baseSymbolToRemove}" removed from blacklist successfully`);
        await loadBlacklist();
      } else {
        setError(response.error || 'Failed to remove from blacklist');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove from blacklist');
    } finally {
      setRemoving(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: isMobile ? '0' : '20px',
        overflowY: 'auto'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: isMobile ? '0' : '12px',
          width: '100%',
          maxWidth: '700px',
          maxHeight: isMobile ? '100vh' : '90vh',
          height: isMobile ? '100vh' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f9fafb'
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 600,
              color: '#111827',
              fontFamily: 'Nunito, sans-serif'
            }}
          >
            Meme Coin Blacklist
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '20px',
            overflowY: 'auto',
            flex: 1
          }}
        >
          {/* Success Message */}
          {successMessage && (
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: '#dcfce7',
                border: '1px solid #86efac',
                borderRadius: '8px',
                marginBottom: '16px',
                color: '#166534',
                fontSize: '14px',
                fontFamily: 'Mukta, sans-serif'
              }}
            >
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                marginBottom: '16px',
                color: '#991b1b',
                fontSize: '14px',
                fontFamily: 'Mukta, sans-serif'
              }}
            >
              {error}
            </div>
          )}

          {/* Current Blacklist Section */}
          <div style={{ marginBottom: '24px' }}>
            <h4
              style={{
                margin: '0 0 12px 0',
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827',
                fontFamily: 'Nunito, sans-serif'
              }}
            >
              Current Blacklist
            </h4>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                <p style={{ marginTop: '12px', color: '#6b7280', fontSize: '14px' }}>Loading blacklist...</p>
              </div>
            ) : blacklist.length === 0 ? (
              <div
                style={{
                  padding: '24px',
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '14px',
                  fontFamily: 'Mukta, sans-serif'
                }}
              >
                No coins are currently blacklisted.
              </div>
            ) : (
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#374151',
                          fontFamily: 'Nunito, sans-serif',
                          borderBottom: '1px solid #e5e7eb'
                        }}
                      >
                        Base Symbol
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#374151',
                          fontFamily: 'Nunito, sans-serif',
                          borderBottom: '1px solid #e5e7eb'
                        }}
                      >
                        Reason
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'right',
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#374151',
                          fontFamily: 'Nunito, sans-serif',
                          borderBottom: '1px solid #e5e7eb',
                          width: '100px'
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {blacklist.map((entry) => (
                      <tr
                        key={entry.id}
                        style={{
                          borderBottom: '1px solid #f3f4f6'
                        }}
                      >
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#111827',
                            fontFamily: 'Mukta, sans-serif'
                          }}
                        >
                          {entry.baseSymbol}
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            fontSize: '14px',
                            color: '#6b7280',
                            fontFamily: 'Mukta, sans-serif'
                          }}
                        >
                          {entry.reason || <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>No reason provided</span>}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <button
                            onClick={() => handleRemove(entry.baseSymbol)}
                            disabled={removing === entry.baseSymbol}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: removing === entry.baseSymbol ? '#f3f4f6' : '#fee2e2',
                              color: removing === entry.baseSymbol ? '#9ca3af' : '#991b1b',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: removing === entry.baseSymbol ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontFamily: 'Mukta, sans-serif',
                              fontWeight: 500,
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (removing !== entry.baseSymbol) {
                                e.currentTarget.style.backgroundColor = '#fecaca';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (removing !== entry.baseSymbol) {
                                e.currentTarget.style.backgroundColor = '#fee2e2';
                              }
                            }}
                          >
                            {removing === entry.baseSymbol ? 'Removing...' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Add to Blacklist Section */}
          <div>
            <h4
              style={{
                margin: '0 0 12px 0',
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827',
                fontFamily: 'Nunito, sans-serif'
              }}
            >
              Add to Blacklist
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#374151',
                    fontFamily: 'Nunito, sans-serif'
                  }}
                >
                  Base Symbol <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={baseSymbol}
                  onChange={(e) => setBaseSymbol(e.target.value.toUpperCase())}
                  placeholder="e.g., PEPE, DOGE"
                  maxLength={10}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Mukta, sans-serif',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#086afb';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                />
                <p style={{ marginTop: '4px', fontSize: '12px', color: '#6b7280', fontFamily: 'Mukta, sans-serif' }}>
                  2-10 characters, alphanumeric only (e.g., PEPE, DOGE, BB)
                </p>
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#374151',
                    fontFamily: 'Nunito, sans-serif'
                  }}
                >
                  Reason (Optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Optional reason for blacklisting..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Mukta, sans-serif',
                    resize: 'vertical',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#086afb';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                />
              </div>
              <button
                onClick={handleAdd}
                disabled={adding || !baseSymbol.trim()}
                style={{
                  padding: '12px 24px',
                  backgroundColor: adding || !baseSymbol.trim() ? '#9ca3af' : '#086afb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  cursor: adding || !baseSymbol.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Mukta, sans-serif',
                  fontWeight: 500,
                  transition: 'all 0.2s',
                  alignSelf: 'flex-start'
                }}
                onMouseEnter={(e) => {
                  if (!adding && baseSymbol.trim()) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!adding && baseSymbol.trim()) {
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                {adding ? 'Adding...' : 'Add to Blacklist'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemeBlacklistModal;
